<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import CampaignsAPI from 'dashboard/api/campaigns';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';
import ExportReportModal from '../ExportReportModal/ExportReportModal.vue';

const props = defineProps({
  campaignId: {
    type: [String, Number],
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits([
  'edit',
  'delete',
  'pause',
  'resume',
  'stop',
  'export',
  'view-details',
]);

const showExportModal = ref(false);
const executionStatus = ref('');

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_PAUSED = 'paused';
const STATUS_STOPPED = 'stopped';
const STATUS_ACTIVE = 'active';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() => {
  if (props.isLiveChatType) return props.isEnabled;
  return props.status === STATUS_ACTIVE;
});

const isPaused = computed(() => props.status === STATUS_PAUSED);
const isStopped = computed(() => props.status === STATUS_STOPPED);
const isCompleted = computed(() => props.status === STATUS_COMPLETED);

const isWhatsAppCampaign = computed(
  () => props.inbox?.channel_type === 'Channel::Whatsapp'
);

const isSending = computed(
  () => isActive.value && executionStatus.value === 'running'
);

const statusTextColor = computed(() => {
  if (isPaused.value) return 'text-n-yellow-11';
  if (isStopped.value) return 'text-n-ruby-11';
  if (isActive.value) return 'text-n-teal-11';
  return 'text-n-slate-12';
});

const statusBadgeClass = computed(() => {
  if (isPaused.value) return 'bg-n-alpha-2 text-n-yellow-11';
  if (isStopped.value) return 'bg-n-alpha-2 text-n-ruby-11';
  if (isActive.value) return 'bg-n-alpha-2 text-n-teal-11';
  return 'bg-n-alpha-2 text-n-slate-12';
});

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (isPaused.value) return t('CAMPAIGN.LIST.STATUS.PAUSED');
  if (isStopped.value) return t('CAMPAIGN.LIST.STATUS.STOPPED');
  if (isCompleted.value) return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  if (isSending.value) return t('CAMPAIGN.LIST.STATUS.SENDING');
  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { phone_number: phoneNumber, channel_type: type } = props.inbox;
  return getInboxIconByType(type, phoneNumber);
});

const canExportReport = computed(
  () => isWhatsAppCampaign.value && (isCompleted.value || isStopped.value)
);

const canPause = computed(() => isWhatsAppCampaign.value && isActive.value);

const canResume = computed(() => isWhatsAppCampaign.value && isPaused.value);

const canStop = computed(
  () =>
    isWhatsAppCampaign.value &&
    (isActive.value || isPaused.value) &&
    !isCompleted.value &&
    !isStopped.value
);

const handleExportClick = () => {
  showExportModal.value = true;
};

onMounted(async () => {
  try {
    if (!isWhatsAppCampaign.value) return;
    const { data } = await CampaignsAPI.deliveryStatus(props.campaignId);
    executionStatus.value = data?.execution?.status || '';
  } catch (e) {
    // silently ignore
  }
});
</script>

<template>
  <CardLayout layout="row">
    <div
      class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2"
      :class="{
        'cursor-pointer hover:bg-n-alpha-1 rounded-lg p-1 -m-1':
          isWhatsAppCampaign,
      }"
      @click="isWhatsAppCampaign && emit('view-details')"
    >
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md"
          :class="statusBadgeClass"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
    </div>
    <div class="flex items-center justify-end gap-2">
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click.stop="emit('edit')"
      />
      <Button
        v-if="canPause"
        variant="faded"
        size="sm"
        color="amber"
        icon="i-lucide-pause"
        :label="t('CAMPAIGN.LIST.BUTTONS.PAUSE')"
        @click.stop="emit('pause')"
      />
      <Button
        v-if="canResume"
        variant="faded"
        size="sm"
        color="teal"
        icon="i-lucide-play"
        :label="t('CAMPAIGN.LIST.BUTTONS.RESUME')"
        @click.stop="emit('resume')"
      />
      <Button
        v-if="canStop"
        variant="faded"
        size="sm"
        color="ruby"
        icon="i-lucide-square"
        :label="t('CAMPAIGN.LIST.BUTTONS.STOP')"
        @click.stop="emit('stop')"
      />
      <Button
        v-if="canExportReport"
        variant="faded"
        size="sm"
        color="blue"
        icon="i-lucide-download"
        :label="t('CAMPAIGN.LIST.BUTTONS.EXPORT_REPORT')"
        @click.stop="handleExportClick"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click.stop="emit('delete')"
      />
    </div>
  </CardLayout>

  <ExportReportModal
    :show="showExportModal"
    :campaign="{
      id: props.campaignId,
      title: props.title,
      campaign_status: props.status,
      inbox: props.inbox,
    }"
    @close="showExportModal = false"
    @export-success="emit('export', props.campaignId)"
    @export-error="error => {}"
  />
</template>
